import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { toast } from "sonner";
import { Users, CheckCircle2, XCircle, Clock, LifeBuoy, Send, ShieldCheck, CreditCard, Trash2 } from "lucide-react";

const STAT = [
  ["total_members", "Total Members", Users, "#59288E"],
  ["active", "Active", CheckCircle2, "#166534"],
  ["trialing", "Trialing", Clock, "#8F7220"],
  ["pending_approval", "Pending Approval", ShieldCheck, "#B45309"],
  ["open_tickets", "Open Tickets", LifeBuoy, "#B42318"],
];
const STATUS_STYLE = {
  active: "bg-[#E7F6EC] text-[#166534]", trialing: "bg-[#FCF3E4] text-[#8F7220]",
  none: "bg-gray-100 text-gray-600", canceled: "bg-[#FDECEC] text-[#B42318]",
  approved: "bg-[#E7F6EC] text-[#166534]", pending: "bg-[#FEF3C7] text-[#B45309]", rejected: "bg-[#FDECEC] text-[#B42318]",
};
const pill = (s) => `text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[s] || "bg-gray-100 text-gray-600"}`;

export default function AdminPortal() {
  const [tab, setTab] = useState("members");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reply, setReply] = useState({});

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data.members)).catch(() => {});
    api.get("/admin/tickets").then((r) => setTickets(r.data.tickets)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setApproval = async (id, action) => {
    await api.post(`/admin/members/${id}/${action}`);
    toast.success(action === "approve" ? "Member approved — access granted" : "Member rejected");
    load();
  };
  const deleteMember = async (m) => {
    if (!window.confirm(`Permanently delete ${m.name || m.email}? This removes their account and all their data. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success("Member deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete member"); }
  };
  const sendReply = async (id) => {
    const msg = (reply[id] || "").trim();
    if (!msg) return;
    await api.post(`/admin/tickets/${id}/reply`, { message: msg });
    setReply((r) => ({ ...r, [id]: "" }));
    toast.success("Reply sent");
    load();
  };

  if (!stats) return <Loader />;
  const pending = members.filter((m) => m.path === "existing" && m.approval_status === "pending");

  return (
    <div data-testid="module-admin">
      <PageHeader eyebrow="Admin" title="Admin Portal" subtitle="Manage members, approvals, and support for WhatsMyTribe.com" />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {STAT.map(([k, label, Icon, color]) => (
          <Panel key={k} className="p-4" >
            <Icon size={18} style={{ color }} />
            <div className="text-2xl font-serif font-bold text-[#180D24] mt-1" data-testid={`stat-${k}`}>{stats[k]}</div>
            <div className="text-xs text-[#756D7E]">{label}</div>
          </Panel>
        ))}
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[["members", `Members (${members.length})`], ["approvals", `Pending Approvals (${pending.length})`], ["tickets", `Support Tickets (${tickets.length})`]].map(([id, label]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-[#2A1442] text-white" : "bg-white border border-[#E4DCD3] text-[#59288E] hover:border-[#D4AF37]"}`}>{label}</button>
        ))}
      </div>

      {tab === "members" && (
        <Panel className="p-0 overflow-x-auto" data-testid="admin-members-table">
          <table className="w-full text-sm">
            <thead className="bg-[#FBF7FF] text-[#59288E]"><tr>
              {["Member", "Email", "Type", "Subscription", "Approval", "Order #", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide">{h}</th>)}
            </tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-t border-[#EFE9E1]" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 font-medium text-[#180D24]">{m.name || "—"}</td>
                  <td className="px-4 py-3 text-[#756D7E]">{m.email}</td>
                  <td className="px-4 py-3 capitalize">{m.path || "—"}</td>
                  <td className="px-4 py-3"><span className={pill(m.membership_status)}>{m.membership_status}</span></td>
                  <td className="px-4 py-3"><span className={pill(m.approval_status)}>{m.approval_status}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{m.order_number || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => deleteMember(m)} data-testid={`delete-member-${m.id}`} title="Delete member"
                      className="inline-flex items-center gap-1 text-[#B42318] hover:bg-[#FDECEC] rounded-lg px-2 py-1 text-xs font-semibold"><Trash2 size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {tab === "approvals" && (
        <div className="space-y-3" data-testid="admin-approvals">
          {pending.length === 0 ? <Panel className="p-8 text-center text-[#756D7E]">No Option 1 order numbers awaiting approval. 🎉</Panel> : pending.map((m) => (
            <Panel key={m.id} className="p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`approval-${m.id}`}>
              <div>
                <div className="font-serif text-lg font-semibold text-[#180D24]">{m.name} <span className="text-sm font-normal text-[#756D7E]">· {m.email}</span></div>
                <div className="text-sm text-[#8F7220] font-mono mt-1">Order #: {m.order_number || "—"} · Klan: {m.tribal_klan || "—"}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setApproval(m.id, "approve")} data-testid={`approve-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#166534] text-white px-4 py-2 text-sm font-bold hover:bg-[#12532b]"><CheckCircle2 size={15} /> Approve</button>
                <button onClick={() => setApproval(m.id, "reject")} data-testid={`reject-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5484D] text-[#B42318] px-4 py-2 text-sm font-bold hover:bg-[#FDECEC]"><XCircle size={15} /> Reject</button>
              </div>
            </Panel>
          ))}
        </div>
      )}

      {tab === "tickets" && (
        <div className="space-y-3" data-testid="admin-tickets">
          {tickets.length === 0 ? <Panel className="p-8 text-center text-[#756D7E]">No support tickets yet.</Panel> : tickets.map((t) => (
            <Panel key={t.id} className="p-5" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="font-serif text-lg font-semibold text-[#180D24]">{t.subject}</div>
                <span className={pill(t.status)}>{t.status}</span>
              </div>
              <div className="text-xs text-[#756D7E] mb-3">{t.user_name} · {t.user_email}</div>
              <div className="space-y-2 mb-3">
                {t.messages.map((msg, i) => (
                  <div key={i} className={`rounded-lg px-3 py-2 text-sm max-w-[85%] ${msg.from === "admin" ? "ml-auto bg-[#F3EEFA] text-[#2A1442]" : "bg-[#FAF8F5] text-[#1A171D]"}`}>
                    <div className="text-[10px] font-bold uppercase text-[#8F7220] mb-0.5">{msg.from === "admin" ? "Admin" : t.user_name}</div>
                    {msg.text}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={reply[t.id] || ""} onChange={(e) => setReply((r) => ({ ...r, [t.id]: e.target.value }))} data-testid={`reply-input-${t.id}`}
                  placeholder="Type your reply…" className="flex-1 rounded-lg border border-[#E4DCD3] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50" />
                <button onClick={() => sendReply(t.id)} data-testid={`reply-send-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#2A1442] text-white px-4 py-2 text-sm font-bold hover:bg-[#482276]"><Send size={14} /> Reply</button>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
